/**
 * FacilityLensResult -- displays AI identification, knowledge, and smart actions.
 *
 * Two modes based on user persona:
 *   Report Mode (end user): Interactive symptom troubleshooting flow
 *   Fix Mode (tech/manager): Common issues, specs, manuals, tips
 */

import { useState, useEffect, Component, type ReactNode } from "react";
import {
	Button,
	Tag,
	Collapse,
	Checkbox,
	Input,
	Select,
	Modal,
	message,
	Spin,
	Segmented,
} from "antd";
import {
	CheckCircleOutlined,
	WarningOutlined,
	ExclamationCircleOutlined,
	BookOutlined,
	ToolOutlined,
	LinkOutlined,
	PlusOutlined,
	ScanOutlined,
	BulbOutlined,
	SafetyCertificateOutlined,
	AppstoreAddOutlined,
	EyeOutlined,
	InfoCircleOutlined,
	UserOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useFacilityLensStore, type ResultMode } from "@/stores/facility-lens-store";
import { useSoulStore } from "@/stores/soul-store";
import {
	flCreateWorkOrder,
	flLinkWorkOrder,
	flMatchAssets,
	type FacilityLensScan,
	type KnowledgeIssue,
	type MatchedAsset,
	type SymptomCard,
} from "@/services/facility-lens-service";
import FacilityLensTroubleshoot from "./FacilityLensTroubleshoot";
import "./FacilityLensResult.scss";

const { TextArea } = Input;

// ─── Error Boundary ──────────────────────────────────────────────────────────
class ResultErrorBoundary extends Component<
	{ children: ReactNode; onReset: () => void },
	{ hasError: boolean; error: string }
> {
	state = { hasError: false, error: "" };

	static getDerivedStateFromError(error: Error) {
		return { hasError: true, error: error.message };
	}

	render() {
		if (this.state.hasError) {
			return (
				<div style={{ textAlign: "center", padding: 40 }}>
					<h3>Something went wrong displaying results</h3>
					<p style={{ color: "#888", fontSize: 14 }}>{this.state.error}</p>
					<Button type="primary" onClick={this.props.onReset}>
						Start Over
					</Button>
				</div>
			);
		}
		return this.props.children;
	}
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const SEVERITY_CONFIG: Record<string, { color: string; icon: React.ReactNode }> = {
	LOW: { color: "blue", icon: <CheckCircleOutlined /> },
	MEDIUM: { color: "gold", icon: <ExclamationCircleOutlined /> },
	HIGH: { color: "orange", icon: <WarningOutlined /> },
	CRITICAL: { color: "red", icon: <WarningOutlined /> },
};

function safeArray<T>(val: unknown): T[] {
	return Array.isArray(val) ? val : [];
}

function safeObject(val: unknown): Record<string, unknown> {
	return val && typeof val === "object" && !Array.isArray(val)
		? (val as Record<string, unknown>)
		: {};
}

type MatchCase = "A" | "B" | "C";

function determineCase(
	ai: FacilityLensScan["aiIdentification"],
	bestMatch: MatchedAsset | null,
): MatchCase {
	if (bestMatch) return "A";
	const hasDetails = ai && (ai.model || (ai as any).serialNumber || (ai as any).assetTag);
	if (hasDetails) return "B";
	return "C";
}

/** Determine default result mode from soul persona */
function defaultModeFromPersona(persona?: string | null): ResultMode {
	if (!persona) return "report";
	const reportPersonas = ["location_worker", "other"];
	return reportPersonas.includes(persona) ? "report" : "fix";
}

// ─── Component ───────────────────────────────────────────────────────────────

interface FacilityLensResultProps {
	scan: FacilityLensScan;
	onStartOver: () => void;
}

const FacilityLensResultInner = ({ scan, onStartOver }: FacilityLensResultProps) => {
	const navigate = useNavigate();
	const { selectedIssueIndices, toggleIssue, resultMode, setResultMode } = useFacilityLensStore();
	const { soul } = useSoulStore();
	const ai = scan.aiIdentification;

	// Asset matching state
	const [matchLoading, setMatchLoading] = useState(true);
	const [bestMatch, setBestMatch] = useState<MatchedAsset | null>(null);
	const [matchCase, setMatchCase] = useState<MatchCase>("C");

	// WO enrichment context from troubleshooting flow
	const [troubleshootContext, setTroubleshootContext] = useState("");

	// Knowledge
	const knowledge = scan.knowledge ?? null;
	const issues = safeArray<KnowledgeIssue>(knowledge?.commonIssues);
	const manuals = safeArray<{ title: string; url: string; source: string; description?: string }>(knowledge?.manualResources);
	const specs = safeObject(knowledge?.specifications);
	const tips = safeArray<string>(knowledge?.troubleshootingTips);
	const symptomCards = safeArray<SymptomCard>(knowledge?.symptomCards);

	// WO creation state
	const [woModalOpen, setWoModalOpen] = useState(false);
	const [woTitle, setWoTitle] = useState(
		ai ? `FacilityLens: ${ai.assetType}${ai.manufacturer ? ` - ${ai.manufacturer}` : ""}` : "FacilityLens Scan",
	);
	const [woDescription, setWoDescription] = useState("");
	const [woPriority, setWoPriority] = useState(4);
	const [woCreating, setWoCreating] = useState(false);

	// Link WO state
	const [linkModalOpen, setLinkModalOpen] = useState(false);
	const [linkWoId, setLinkWoId] = useState<number | undefined>();
	const [linking, setLinking] = useState(false);

	// Set default mode from persona on mount
	useEffect(() => {
		setResultMode(defaultModeFromPersona(soul?.persona));
	}, []);

	// Fetch asset matches on mount
	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const result = await flMatchAssets(scan.id);
				if (cancelled) return;
				setBestMatch(result.bestMatch);
				setMatchCase(determineCase(ai, result.bestMatch));
			} catch (err) {
				console.error("[FacilityLensResult] Match assets failed:", err);
				if (!cancelled) setMatchCase(determineCase(ai, null));
			} finally {
				if (!cancelled) setMatchLoading(false);
			}
		})();
		return () => { cancelled = true; };
	}, [scan.id]);

	const handleCreateWO = async () => {
		setWoCreating(true);
		try {
			// Build enriched description with troubleshooting context
			let fullDescription = woDescription;
			if (troubleshootContext) {
				fullDescription = troubleshootContext + (woDescription ? `\n\n**Additional Notes:**\n${woDescription}` : "");
			}

			const result = await flCreateWorkOrder(scan.id, {
				title: woTitle,
				description: fullDescription,
				locationId: scan.locationId ?? undefined,
				priority: woPriority,
				workType: "CORRECTIVE",
				selectedIssueIndices,
			});
			message.success("Work order created!");
			setWoModalOpen(false);
			navigate(`/work-orders/${result.workOrderId}`);
		} catch (err: any) {
			message.error(err.message || "Failed to create work order");
		} finally {
			setWoCreating(false);
		}
	};

	const handleLinkWO = async () => {
		if (!linkWoId) return;
		setLinking(true);
		try {
			await flLinkWorkOrder(scan.id, linkWoId);
			message.success("Linked to work order!");
			setLinkModalOpen(false);
			navigate(`/work-orders/${linkWoId}`);
		} catch (err: any) {
			message.error(err.message || "Failed to link");
		} finally {
			setLinking(false);
		}
	};

	const handleAddAsNewAsset = () => {
		const params = new URLSearchParams();
		if (ai?.assetType) params.set("assetType", ai.assetType);
		if (ai?.manufacturer) params.set("manufacturer", ai.manufacturer);
		if (ai?.model) params.set("model", ai.model);
		if ((ai as any)?.serialNumber) params.set("serialNumber", (ai as any).serialNumber);
		if (scan.locationId) params.set("locationId", String(scan.locationId));
		if (scan.primaryPhotoUrl) params.set("photoUrl", scan.primaryPhotoUrl);
		params.set("source", "facility-lens");
		params.set("scanId", String(scan.id));
		navigate(`/assets/new?${params.toString()}`);
	};

	const handleViewAsset = () => {
		if (bestMatch) navigate(`/assets/${bestMatch.id}`);
	};

	// Called from troubleshoot component when user wants to create a WO
	const handleTroubleshootEscalate = (context: string) => {
		setTroubleshootContext(context);
		setWoModalOpen(true);
	};

	const confidenceColor =
		(ai?.confidence ?? 0) >= 80 ? "#52c41a" :
		(ai?.confidence ?? 0) >= 60 ? "#faad14" : "#ff4d4f";

	// Build collapse items for Fix Mode
	const specEntries = Object.entries(specs).filter(([, v]) => v != null);

	return (
		<div className="flr-container">
			{/* ─── Header: Photo + Identification ─── */}
			<div className="flr-id-header">
				<div className="flr-photo-thumb">
					<img src={scan.primaryPhotoUrl} alt="Scanned asset" />
				</div>
				<div className="flr-id-info">
					<h2>{ai?.assetType || "Unknown Asset"}</h2>
					{ai?.manufacturer && (
						<span className="flr-manufacturer">{ai.manufacturer}</span>
					)}
					{ai?.model && (
						<span className="flr-model">Model: {ai.model}</span>
					)}
					<div className="flr-meta-row">
						{ai?.condition && (
							<Tag
								color={
									ai.condition === "GOOD" ? "green" :
									ai.condition === "FAIR" ? "gold" :
									ai.condition === "POOR" ? "orange" : "red"
								}
							>
								{ai.condition}
							</Tag>
						)}
						<span className="flr-confidence" style={{ color: confidenceColor }}>
							{ai?.confidence ?? 0}% confidence
						</span>
						{scan.resolvedTier && (
							<Tag
								color={
									scan.resolvedTier === "HASH_MATCH" ? "green" :
									scan.resolvedTier === "QUICK_TRIAGE_CACHE" ? "cyan" :
									scan.resolvedTier === "FAST_ANALYSIS" ? "blue" : "purple"
								}
								style={{ marginLeft: 4, fontSize: 11 }}
							>
								{scan.resolvedTier === "HASH_MATCH"
									? "Instant Match"
									: scan.resolvedTier === "QUICK_TRIAGE_CACHE"
									? "Quick Match"
									: scan.resolvedTier === "FAST_ANALYSIS"
									? "Fast Analysis"
									: "Deep Analysis"}
							</Tag>
						)}
					</div>
					{ai?.visibleIssues && Array.isArray(ai.visibleIssues) && ai.visibleIssues.length > 0 && (
						<div className="flr-visible-issues">
							{ai.visibleIssues.map((issue, i) => (
								<Tag key={i} color="volcano">{String(issue)}</Tag>
							))}
						</div>
					)}
				</div>
			</div>

			{ai?.rawDetails && (
				<p className="flr-raw-details">{ai.rawDetails}</p>
			)}

			{/* ─── Asset Match Banner ─── */}
			{matchLoading ? (
				<div className="flr-match-banner flr-match-loading">
					<Spin size="small" /> <span>Checking for existing assets...</span>
				</div>
			) : matchCase === "A" && bestMatch ? (
				<div className="flr-match-banner flr-match-found">
					<CheckCircleOutlined className="flr-match-icon" />
					<div className="flr-match-text">
						<strong>This matches {bestMatch.assetTypeName || "an asset"}{bestMatch.manufacturerName ? ` by ${bestMatch.manufacturerName}` : ""}</strong>
						{bestMatch.locationName && (
							<span className="flr-match-location">at {bestMatch.locationName}</span>
						)}
						{bestMatch.assetTag && (
							<span className="flr-match-detail">Tag: {bestMatch.assetTag}</span>
						)}
						{bestMatch.serialNumber && (
							<span className="flr-match-detail">S/N: {bestMatch.serialNumber}</span>
						)}
					</div>
					<Button type="link" size="small" onClick={handleViewAsset} icon={<EyeOutlined />}>
						View
					</Button>
				</div>
			) : matchCase === "B" ? (
				<div className="flr-match-banner flr-match-new">
					<InfoCircleOutlined className="flr-match-icon" />
					<div className="flr-match-text">
						<strong>This asset isn't in your system yet</strong>
						<span className="flr-match-detail">You can add it as a new asset from the actions below.</span>
					</div>
				</div>
			) : matchCase === "C" ? (
				<div className="flr-match-banner flr-match-partial">
					<ExclamationCircleOutlined className="flr-match-icon" />
					<div className="flr-match-text">
						<strong>We identified the type but couldn't read specific details</strong>
						<span className="flr-match-detail">You can still create a work order or add this as a new asset.</span>
					</div>
				</div>
			) : null}

			{/* ─── Mode Toggle ─── */}
			<div className="flr-mode-toggle">
				<Segmented
					value={resultMode}
					onChange={(val) => setResultMode(val as ResultMode)}
					options={[
						{
							value: "report",
							label: (
								<span className="flr-mode-label">
									<UserOutlined /> Report a Problem
								</span>
							),
						},
						{
							value: "fix",
							label: (
								<span className="flr-mode-label">
									<ToolOutlined /> Diagnose & Fix
								</span>
							),
						},
					]}
					block
					className="flr-mode-segmented"
				/>
			</div>

			{/* ─── Report Mode: Interactive Troubleshooting ─── */}
			{resultMode === "report" && (
				<>
					<div className="flr-solve-cta">
						<BulbOutlined style={{ fontSize: 20, color: "#fa8c16" }} />
						<div className="flr-solve-cta-text">
							<strong>Need help diagnosing this issue?</strong>
							<span>Let FacilitySolve AI walk you through step-by-step troubleshooting</span>
						</div>
						<Button
							type="primary"
							icon={<BulbOutlined />}
							onClick={() => {
								const params = new URLSearchParams();
								params.set("autoStart", "1");
								params.set("scanId", String(scan.id));
								params.set("title", ai?.assetType ? `Issue with ${ai.assetType}` : scan.title ?? "Facility Issue");
								if (scan.locationId) params.set("locationId", String(scan.locationId));
								if (ai?.category) params.set("category", ai.category);
								navigate(`/work-orders/triage?${params.toString()}`);
							}}
							style={{ background: "#fa8c16", borderColor: "#fa8c16" }}
						>
							Diagnose with AI
						</Button>
					</div>

					{symptomCards.length > 0 ? (
						<FacilityLensTroubleshoot
							symptomCards={symptomCards}
							onCreateWorkOrder={handleTroubleshootEscalate}
						/>
					) : (
						<div className="flr-no-symptoms">
							<p>No interactive troubleshooting available for this asset type yet.</p>
							<p>You can still create a work order to report the issue.</p>
						</div>
					)}

					{/* Collapsed reference sections in report mode */}
					{(issues.length > 0 || manuals.length > 0 || specEntries.length > 0 || tips.length > 0) && (
						<Collapse
							ghost
							className="flr-collapse flr-report-extras"
							items={[
								...(issues.length > 0 ? [{
									key: "issues",
									label: <span><WarningOutlined /> Common Issues ({issues.length})</span>,
									children: (
										<div className="flr-issues-list">
											{issues.map((issue, idx) => {
												const sev = SEVERITY_CONFIG[issue.severity] || SEVERITY_CONFIG.MEDIUM;
												return (
													<div key={idx} className="flr-issue-card-compact">
														<Tag color={sev.color} icon={sev.icon} className="flr-sev-tag">{issue.severity}</Tag>
														<span className="flr-issue-title">{issue.title}</span>
													</div>
												);
											})}
										</div>
									),
								}] : []),
								...(manuals.length > 0 ? [{
									key: "manuals",
									label: <span><BookOutlined /> Resource Manuals ({manuals.length})</span>,
									children: renderManuals(manuals),
								}] : []),
								...(specEntries.length > 0 ? [{
									key: "specs",
									label: <span><SafetyCertificateOutlined /> Specifications</span>,
									children: renderSpecs(specEntries),
								}] : []),
							]}
						/>
					)}
				</>
			)}

			{/* ─── Fix Mode: Tech-Focused Sections ─── */}
			{resultMode === "fix" && (
				<>
					{/* 1. Common Issues */}
					{issues.length > 0 && (
						<div className="flr-section">
							<h3><WarningOutlined /> Common Issues</h3>
							<p className="flr-section-desc">
								Select any issues that apply -- they will be attached to the work order.
							</p>
							<div className="flr-issues-list">
								{issues.map((issue, idx) => {
									const sev = SEVERITY_CONFIG[issue.severity] || SEVERITY_CONFIG.MEDIUM;
									const checked = selectedIssueIndices.includes(idx);
									return (
										<div
											key={idx}
											className={`flr-issue-card ${checked ? "flr-issue-selected" : ""}`}
											onClick={() => toggleIssue(idx)}
										>
											<Checkbox checked={checked} className="flr-issue-check" />
											<div className="flr-issue-body">
												<div className="flr-issue-title-row">
													<Tag color={sev.color} icon={sev.icon} className="flr-sev-tag">{issue.severity}</Tag>
													<span className="flr-issue-title">{issue.title}</span>
												</div>
												<p className="flr-issue-desc">{issue.description}</p>
												<p className="flr-issue-solution">
													<BulbOutlined /> {issue.solutionSummary}
												</p>
											</div>
										</div>
									);
								})}
							</div>
						</div>
					)}

					{/* 2. Specifications */}
					{specEntries.length > 0 && (
						<div className="flr-section">
							<h3><SafetyCertificateOutlined /> Specifications</h3>
							{renderSpecs(specEntries)}
						</div>
					)}

					{/* 3. Resource Manuals */}
					{manuals.length > 0 && (
						<div className="flr-section">
							<h3><BookOutlined /> Resource Manuals</h3>
							{renderManuals(manuals)}
						</div>
					)}

					{/* 4. Troubleshooting Tips */}
					{tips.length > 0 && (
						<div className="flr-section">
							<h3><ToolOutlined /> Troubleshooting Tips</h3>
							<ol className="flr-tips-list">
								{tips.map((tip, i) => (
									<li key={i}>{typeof tip === "string" ? tip : JSON.stringify(tip)}</li>
								))}
							</ol>
						</div>
					)}
				</>
			)}

			{/* ─── Smart Action Bar ─── */}
			<div className="flr-action-bar">
				<div className="flr-action-bar-inner">
					<Button
						type="primary"
						size="large"
						icon={<PlusOutlined />}
						onClick={() => setWoModalOpen(true)}
						className="flr-action-primary"
						block
					>
						Create Work Order
					</Button>
					<div className="flr-action-secondary-row">
						{matchCase === "A" && bestMatch ? (
							<>
								<Button size="large" icon={<EyeOutlined />} onClick={handleViewAsset} className="flr-action-secondary">
									View Asset
								</Button>
								<Button size="large" icon={<LinkOutlined />} onClick={() => setLinkModalOpen(true)} className="flr-action-secondary">
									Link to WO
								</Button>
							</>
						) : (
							<>
								<Button size="large" icon={<AppstoreAddOutlined />} onClick={handleAddAsNewAsset} className="flr-action-secondary">
									Add as New Asset
								</Button>
								<Button size="large" icon={<LinkOutlined />} onClick={() => setLinkModalOpen(true)} className="flr-action-secondary">
									Link to WO
								</Button>
							</>
						)}
					</div>
					<Button type="text" icon={<ScanOutlined />} onClick={onStartOver} className="flr-action-tertiary">
						Scan Another Asset
					</Button>
				</div>
			</div>

			{/* ─── Create WO Modal ─── */}
			<Modal
				title="Create Work Order from Scan"
				open={woModalOpen}
				onCancel={() => setWoModalOpen(false)}
				onOk={handleCreateWO}
				confirmLoading={woCreating}
				okText="Create Work Order"
			>
				<div className="flr-wo-form">
					{troubleshootContext && (
						<div className="flr-wo-troubleshoot-context">
							<strong>Troubleshooting context included:</strong>
							<pre>{troubleshootContext}</pre>
						</div>
					)}
					<div className="flr-wo-field">
						<label>Title</label>
						<Input value={woTitle} onChange={(e) => setWoTitle(e.target.value)} />
					</div>
					<div className="flr-wo-field">
						<label>Priority</label>
						<Select
							value={woPriority}
							onChange={setWoPriority}
							style={{ width: "100%" }}
							options={[
								{ value: 1, label: "P1 -- Emergency" },
								{ value: 2, label: "P2 -- Urgent" },
								{ value: 3, label: "P3 -- High" },
								{ value: 4, label: "P4 -- Medium" },
								{ value: 5, label: "P5 -- Low" },
							]}
						/>
					</div>
					<div className="flr-wo-field">
						<label>Additional Notes</label>
						<TextArea
							rows={3}
							value={woDescription}
							onChange={(e) => setWoDescription(e.target.value)}
							placeholder="Any additional context..."
						/>
					</div>
					{selectedIssueIndices.length > 0 && (
						<p className="flr-wo-issues-note">
							{selectedIssueIndices.length} issue(s) will be attached to this work order.
						</p>
					)}
					{matchCase === "A" && bestMatch && (
						<p className="flr-wo-asset-note">
							<CheckCircleOutlined style={{ color: "#52c41a" }} />{" "}
							This work order will be linked to existing asset #{bestMatch.id}
						</p>
					)}
				</div>
			</Modal>

			{/* ─── Link WO Modal ─── */}
			<Modal
				title="Link to Existing Work Order"
				open={linkModalOpen}
				onCancel={() => setLinkModalOpen(false)}
				onOk={handleLinkWO}
				confirmLoading={linking}
				okText="Link"
			>
				<div className="flr-wo-field">
					<label>Work Order ID</label>
					<Input
						type="number"
						placeholder="Enter work order ID..."
						value={linkWoId}
						onChange={(e) => setLinkWoId(parseInt(e.target.value, 10) || undefined)}
					/>
				</div>
			</Modal>
		</div>
	);
};

// ─── Shared Render Helpers ───────────────────────────────────────────────────

function renderManuals(manuals: { title: string; url: string; source: string; description?: string }[]) {
	return (
		<div className="flr-manuals-list">
			{manuals.map((manual, idx) => (
				<a key={idx} href={manual.url} target="_blank" rel="noopener noreferrer" className="flr-manual-card">
					<div className="flr-manual-icon"><LinkOutlined /></div>
					<div className="flr-manual-info">
						<span className="flr-manual-title">{manual.title}</span>
						<span className="flr-manual-source">{manual.source}</span>
						{manual.description && <span className="flr-manual-desc">{manual.description}</span>}
					</div>
				</a>
			))}
		</div>
	);
}

function renderSpecs(specEntries: [string, unknown][]) {
	return (
		<div className="flr-specs">
			{specEntries.map(([key, value]) => {
				const label = key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase());
				return (
					<div key={key} className="flr-spec-row">
						<span className="flr-spec-label">{label}</span>
						<span className="flr-spec-value">
							{Array.isArray(value) ? value.join(", ") : String(value)}
						</span>
					</div>
				);
			})}
		</div>
	);
}

// ─── Export with Error Boundary ──────────────────────────────────────────────

const FacilityLensResult = (props: FacilityLensResultProps) => (
	<ResultErrorBoundary onReset={props.onStartOver}>
		<FacilityLensResultInner {...props} />
	</ResultErrorBoundary>
);

export default FacilityLensResult;
